import React, {
	useState,
	useEffect,
	useRef,
	useMemo,
  useCallback,
} from "react";
import { Box, CircularProgress, Alert } from "@mui/material";
import { Gantt, Willow } from "wx-react-gantt";
import { useTranslation } from "react-i18next";
import { useFilters } from "components/Elements/Filters";

import { useGanttData } from "./hooks/useGanttData";
import { useGanttZoom } from "./hooks/useGanttScale";
import useGanttEventHandlers from "./hooks/useGanttEventHandlers";
import { useGanttViewport } from "./hooks/useGanttViewport";
import useGanttColumns from "./hooks/useGanttColumns";
import GanttToolbar from "./components/GanttToolbar";
import GanttTaskContent from "./components/GanttTaskContent";
import { calculateDateRange } from "./utils/dateUtils";
import ColourConstants from "helpers/colourConstants";
import "./styles/ganttTheme.css";

const ganttContainerStyles = {
	height: "calc(100vh - 300px)",
	minHeight: "400px",
	width: "100%",
	display: "flex",
	flexDirection: "column",
	border: `1px solid ${ColourConstants.tableBorder}`,
	borderRadius: 0,
	backgroundColor: "#fff",
};

const ganttChartStyles = {
	flex: 1,
	overflow: "auto",
	position: "relative",
};

const loadingOverlayStyles = {
	display: "flex",
	justifyContent: "center",
	alignItems: "center",
	p: 2,
	backgroundColor: "rgba(255, 255, 255, 0.9)",
	borderTop: `1px solid ${ColourConstants.tableBorder}`,
	position: "sticky",
	bottom: 0,
};

const emptyStateStyles = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	textAlign: "center",
	color: "#666",
	fontSize: "0.875rem",
};

const ServicesGantt = () => {
	const { t } = useTranslation();
	const ganttRef = useRef();
	const apiRef = useRef();
	const [zoomLevel, setZoomLevel] = useState(null);
	const [initialZoomCalculated, setInitialZoomCalculated] = useState(false);
	const [ganttApi, setGanttApi] = useState(null);

	// Get filter values from context
	const { buildUrlParams } = useFilters();

	// Use buildUrlParams to get properly formatted filter parameters
	const filters = useMemo(() => {
		const params = buildUrlParams();
		return {
			...params,
			chips: params.chips || [],
		};
	}, [buildUrlParams]);

	const {
		ganttData: tasks,
		taskTypes,
		services,
		loading,
		loadingMore,
		error,
		hasMorePages,
		loadMore,
	} = useGanttData(filters);

	const { scrollContainerRef } = useGanttViewport({
		loadMore,
		loadingMore,
		hasMorePages,
		servicesLength: services.length
	});


	// Double-click handler to open service in new tab
	const handleTaskSelect = useCallback((taskId) => {
		const task = tasks.find(t => t.id === taskId);
		// Only allow opening child tasks (not parent work order tasks)
		if (task && task.serviceData && task.parent) {
			const serviceId = task.serviceData.id;
			window.open(`/app/services/${serviceId}`, '_blank');
		}
	}, [tasks]);

	// Zoom handlers - 5 levels: 0=multi-year, 1=year, 2=month, 3=week, 4=day/hour
	const handleZoomIn = () => {
		// Get current zoom from Gantt API if available
		const currentZoom = apiRef.current?.getState()?.zoom?.level;
		
		setZoomLevel((prev) => {
			const actualLevel = currentZoom !== undefined ? currentZoom : prev;
			const maxLevel = initialZoomConfig?.levels?.length
				? initialZoomConfig.levels.length - 1
				: 4;
			if (actualLevel === null) return Math.min(2, maxLevel); // Default to month view
			if (actualLevel >= maxLevel) return maxLevel; // Can't zoom in more than max level
			return actualLevel + 1;
		});
	};

	const handleZoomOut = () => {
		// Get current zoom from Gantt API if available
		const currentZoom = apiRef.current?.getState()?.zoom?.level;
		
		setZoomLevel((prev) => {
			const actualLevel = currentZoom !== undefined ? currentZoom : prev;
			if (actualLevel === null || actualLevel === 0) {
				return 0; // Can't zoom out more than level 0
			}
			return actualLevel - 1;
		});
	};

	// Reset zoom calculation flag when filters change
	useEffect(() => {
		setInitialZoomCalculated(false);
	}, [filters]);

	const dateRange = useMemo(() => calculateDateRange(tasks), [tasks]);

	// Get initial scale based on actual services data - only calculate if we haven't set initial zoom yet
	const initialZoomConfig = useGanttZoom(initialZoomCalculated ? [] : services);

	useGanttEventHandlers({
		ganttApi: ganttApi,
		onTaskSelect: handleTaskSelect,
	});

	// Initialize zoom level from timeframe when it changes - only on first calculation
	useEffect(() => {
		if (initialZoomConfig?.level !== undefined && !initialZoomCalculated) {
			setZoomLevel(initialZoomConfig.level);
			setInitialZoomCalculated(true);
		}
	}, [initialZoomConfig?.level, initialZoomCalculated]);


	// Build zoom config with current zoom level
	const zoomConfig = useMemo(() => {
		if (zoomLevel === null) return initialZoomConfig;
		
		return {
			...initialZoomConfig,
			level: zoomLevel,
		};
	}, [zoomLevel, initialZoomConfig]);

	// Get columns configuration
	const columns = useGanttColumns();


	if (loading && services.length === 0) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (error && services.length === 0) {
		return (
			<Box sx={{ p: 2 }}>
				<Alert severity="error">{t("message.failedToLoadServicesData")}</Alert>
			</Box>
		);
	}

	return (
		<Box sx={ganttContainerStyles}>
			{/* Toolbar with status */}
			<GanttToolbar
				onZoomIn={handleZoomIn}
				onZoomOut={handleZoomOut}
				statusText={t("label.showingServicesCount", {
					count: services.length,
				})}
			/>

			<Box
				ref={scrollContainerRef}
				sx={ganttChartStyles}
        className="emi-gantt-theme hide-drag hide-progress"
			>
				<Willow>
					<Gantt
						ref={ganttRef}
						api={apiRef}
						init={(api) => {
							// Store the API object  
							apiRef.current = api;
							setGanttApi(api); // Trigger re-render for event handlers
						}}
						tasks={tasks}
						columns={columns}
						zoom={zoomConfig}
						taskTemplate={GanttTaskContent}
						taskTypes={taskTypes}
						lengthUnit={"hour"}
						start={dateRange.start}
						end={dateRange.end}
						readonly={true}
						editor={false}
					/>
				</Willow>

				{!loading && tasks.length === 0 && (
					<Box sx={emptyStateStyles}>
						{t("message.noServicesFoundForFilters")}
					</Box>
				)}

				{loadingMore && (
					<Box sx={loadingOverlayStyles}>
						<CircularProgress size={20} sx={{ mr: 1 }} />
						<Box sx={{ fontSize: "0.875rem", color: "#666" }}>
							{t("message.loadingMoreServices")}
						</Box>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default ServicesGantt;
